import type { Checkout } from 'core/domains';
import { useCommand } from 'core/use-cases/utilities';

import { actions } from '../actions';

/**
 * Exports Checkout dispatch commands.
 *
 */
export function useCheckoutCommand() {
  const { dispatchCommand } = useCommand();

  /**
   * Clean up page from store to avoid memory leaks
   *
   */
  function cleanCheckout() {
    dispatchCommand(actions.checkoutCleanUp());
  }

  /**
   * Should dispatch an action to complete the order:
   *
   * @paymentDetailFetch: Should dispatch an action for BFF to finalize the order in Gera.
   * @createOrderFetch: Should dispatch an action for BFF complete the order in your system and clear catching.
   */
  function completeOrder({
    creditCardSchema,
    installments,
    paymentCode,
    paymentMode,
  }: Checkout['paymentOption']) {
    dispatchCommand(
      actions.paymentDetailFetch.request({
        creditCardSchema,
        installments,
        paymentCode,
        paymentMode,
      }),
    );
  }

  /**
   * Initial flow of checkout
   *
   */
  function initCheckoutFlow() {
    dispatchCommand(actions.checkoutInit());
  }

  /**
   * Should dispatch an action to fetch the payment methods again
   *
   */
  function reloadPaymentMethods() {
    dispatchCommand(actions.reloadPaymentMethods());
  }

  /**
   * Should dispatch an action with the payment credit card option selected
   *
   */
  function setCreditCardCompleted(paymentOption: Checkout['paymentOption']) {
    dispatchCommand(actions.setCreditCardCompleted(paymentOption));
  }

  /**
   * Should dispatch an action with the payment credit card option selected
   *
   */
  function setCreditCardOption(paymentOption: Checkout['paymentOption']) {
    dispatchCommand(actions.setCreditCardOption(paymentOption));
  }

  /**
   * Should dispatch an action with the payment option selected
   *
   */
  function setBankSlipOption(paymentCode: Checkout['paymentOption']['paymentCode']) {
    dispatchCommand(actions.setBankSlipOption(paymentCode));
  }

  /**
   * Should dispatch an action with the payment option selected
   *
   */
  function setPayWithdrawalsOption(paymentCode: Checkout['paymentOption']['paymentCode']) {
    dispatchCommand(actions.setPayWithdrawalsOption(paymentCode));
  }

  /**
   * Should dispatch an action with the payment option selected
   *
   */
  function setCentralPaymentOption(paymentCode: Checkout['paymentOption']['paymentCode']) {
    dispatchCommand(actions.setCentralPaymentOption(paymentCode));
  }

  /**
   * Set payment method status
   *
   */
  function setPaymentMethodStatus() {
    dispatchCommand(actions.setPaymentMethodStatus());
  }

  /**
   * Set step of checkout flux
   *
   */
  function setStep(step: Checkout['step']) {
    dispatchCommand(actions.setStep(step));
  }

  /**
   * Set view of checkout flux
   *
   */
  function setView(view: string) {
    dispatchCommand(actions.setView(view));
  }

  /**
   * Show or Hide notification change payment
   *
   */
  function toggleNotification() {
    dispatchCommand(actions.toggleNotification());
  }

  return {
    cleanCheckout,
    completeOrder,
    initCheckoutFlow,
    reloadPaymentMethods,
    setBankSlipOption,
    setCreditCardCompleted,
    setCreditCardOption,
    setPayWithdrawalsOption,
    setPaymentMethodStatus,
    setCentralPaymentOption,
    setStep,
    setView,
    toggleNotification,
  };
}
